/* eslint-disable react/prop-types */
import * as React from 'react';
import { classes, useCombinedRefs, useSlug } from '@bloomberg/media-ui-core';

import { DEFAULT_ASPECT_RATIO } from './constants';
import { MediaPlayer, VideoPlayerProps } from './types';
import { useBPlayer } from './useBPlayer';
import { getPosterImageUrl, parseAspectRatio } from './utils';

import styles from './styles.module.css';

export const VideoPlayer = React.forwardRef<HTMLElement, VideoPlayerProps>(
  function VideoPlayerComponent(
    props: VideoPlayerProps,
    ref: React.ForwardedRef<HTMLElement>
  ) {
    const { name = 'videoplayer', playerRef } = props;

    const [slug] = useSlug(name);

    const videoPlayerContainerEl = React.useRef<HTMLDivElement | null>(null);

    // combine local ref with passed in forwardRef
    const combinedRef = useCombinedRefs<HTMLElement>(
      videoPlayerContainerEl,
      ref
    );

    const viewportAspectRatio = parseAspectRatio(
      props.options.aspectRatio || DEFAULT_ASPECT_RATIO
    );
    const intrinsicAspectRatio = props.options.intrinsicAspectRatio
      ? parseAspectRatio(props.options.intrinsicAspectRatio)
      : viewportAspectRatio;
    const fillPosition = props.options.fillPosition || 'center';
    const fillContainer = props.options.fillContainer;

    // base classes
    const cx = classes([styles.wrapper], {
      ...props,
      name,
    });

    // run a dynamic import for the player
    // and inject player into provided ref
    const { loaded: _loaded, player } = useBPlayer(combinedRef, props);

    // if consumer passed a ref to populate player with
    // set that here
    if (player && playerRef) {
      playerRef.current = player as MediaPlayer;
    }

    React.useEffect(() => {
      if (player && props.description) {
        player.ready(() => {
          // NOTE(calbachten): figure innerHTML is replaced by player
          // so we must reappend figcaption to avoid breaking HTML changes
          const description = document.createElement('figcaption');

          description.innerText = props.description as string;

          description.className = styles.description;

          combinedRef.current?.appendChild(description);
        });
      }
    }, [props.description, player]);

    const poster = props.options.poster;
    const resizedPoster =
      poster && getPosterImageUrl(poster, intrinsicAspectRatio);
    const videoStyle: React.CSSProperties | undefined = fillContainer
      ? {
          objectFit: 'cover',
          objectPosition: fillPosition,
        }
      : undefined;

    return (
      <figure
        style={{
          aspectRatio: `${viewportAspectRatio.width}/${viewportAspectRatio.height}`,
        }}
        className={cx}
        ref={combinedRef}
        data-component={slug}
      >
        {resizedPoster && (
          <video
            style={videoStyle}
            controls={false}
            poster={resizedPoster}
            className={styles.posterVideo}
          >
            <source src="" type="video/webm" />
            <track default kind="captions" src="" />
          </video>
        )}
        {props.description && (
          <figcaption className={styles.description}>
            {props.description}
          </figcaption>
        )}
      </figure>
    );
  }
);
